import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { cleanupSuperAdminJobs } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import type { AdminDiagnostics, AdminJob } from "@/lib/types";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { AdminMetricTile } from "@/components/console/AdminMetricTile";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { useOperatorDiagnostics, useOperatorJobs } from "../hooks/useOperatorData";

/**
 * Operator Operations — `/super-admin/operations` (admin-console-split PR 4).
 *
 * The "is the platform healthy?" page: cross-club KPI tiles, the system
 * snapshot that used to sit on the club-era `/admin/overview` (disk, failed
 * slots, HLS backfill, upload limits), the background-job queue with its
 * cleanup verb, and the platform-wide upload / error / activity feeds. Deep
 * pipeline recovery lives on Video pipeline; storage forensics on Storage.
 */
export function OperationsPage() {
  const diagQuery = useOperatorDiagnostics();
  const jobsQuery = useOperatorJobs({ refetchIntervalMs: 10_000 });
  const payload = diagQuery.data;
  const counts = payload?.counts;

  return (
    <div data-testid="super-operations" className="flex flex-col gap-4">
      <AdminPageHeader
        eyebrow="Operations"
        title="Platform Health"
        description="Every club's uploads, transcodes, and failures in one place — with the job queue that does the work."
        meta={diagQuery.isFetching ? "Refreshing..." : "Auto-refreshes every 10 s"}
      />

      {diagQuery.error ? (
        <p data-testid="super-operations-error" className="text-xs font-mono text-destructive">
          Failed to load platform diagnostics.
        </p>
      ) : null}

      <section className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <AdminMetricTile
          testid="super-ops-kpi-matches"
          label="Matches"
          value={counts ? String(counts.matches) : "–"}
        />
        <AdminMetricTile
          testid="super-ops-kpi-encoding"
          label="Encoding"
          value={counts ? String(counts.transcoding_slots) : "–"}
          tone={counts && counts.transcoding_slots > 0 ? "busy" : "neutral"}
        />
        <AdminMetricTile
          testid="super-ops-kpi-failed"
          label="Failed slots"
          value={counts ? String(counts.failed_slots) : "–"}
          tone={counts && counts.failed_slots > 0 ? "warn" : "good"}
          sub={counts && counts.failed_slots > 0 ? "Recover in Video pipeline" : undefined}
        />
        <AdminMetricTile
          testid="super-ops-kpi-hls"
          label="HLS missing"
          value={counts ? String(counts.hls_missing_slots) : "–"}
          tone={counts && counts.hls_missing_slots > 0 ? "warn" : "good"}
        />
        <AdminMetricTile
          testid="super-ops-kpi-tokens"
          label="Active tokens"
          value={counts ? String(counts.active_tokens) : "–"}
          sub="Signed-in sessions"
        />
      </section>

      <SystemSnapshot payload={payload} />
      <BackgroundJobsStrip
        jobs={jobsQuery.data?.jobs ?? []}
        counts={jobsQuery.data?.counts ?? {}}
      />
      <UploadSessionsCard payload={payload} />
      <div className="grid gap-3 xl:grid-cols-2">
        <RecentErrorsCard payload={payload} />
        <RecentActivityCard payload={payload} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// System snapshot — ported from the club-era /admin/overview (it leaves that
// page at the club-console cutover; the club never sees disk/backfill again).
// ---------------------------------------------------------------------------

type Tone = "good" | "warn" | "bad" | "busy" | "idle" | "muted";

const TONE_CLASSES: Record<Tone, string> = {
  good: "text-good",
  warn: "text-accent",
  bad: "text-destructive",
  busy: "text-accent",
  idle: "text-ink-muted",
  muted: "text-ink",
};

/** Fail-safe tone for disk-dependent operator surfaces (#212). */
export function diskTone(disk: AdminDiagnostics["disk"] | undefined): Tone {
  if (!disk) return "muted";
  const free = disk.free_bytes;
  const min = disk.min_free_bytes;
  if (typeof free !== "number" || !Number.isFinite(free)) return "bad";
  if (free <= (typeof min === "number" ? min : 0)) return "bad";
  return disk.enough_space ? "good" : "warn";
}

function SystemSnapshot({ payload }: { payload: AdminDiagnostics | undefined }): ReactNode {
  return (
    <section data-testid="super-ops-system" className="flex flex-col gap-2">
      <SectionTitle
        title="System Snapshot"
        detail="Compact host health. Deep controls live in Video pipeline, Storage, and Live infrastructure."
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <SnapshotTile
          testid="kpi-disk"
          label="Free Disk"
          value={payload ? formatBytes(payload.disk.free_bytes) : "-"}
          sub={payload ? `minimum ${formatBytes(payload.disk.min_free_bytes)}` : ""}
          tone={diskTone(payload?.disk)}
        />
        <SnapshotTile
          testid="kpi-failed"
          label="Failed Slots"
          value={payload ? String(payload.counts.failed_slots) : "-"}
          sub={payload ? `${payload.recent_errors.length} recent errors` : ""}
          tone={(payload?.counts.failed_slots ?? 0) > 0 ? "bad" : "good"}
        />
        <SnapshotTile
          testid="kpi-hls-backfill"
          label="HLS Backfill"
          value={payload?.hls.backfill_running ? "Running" : "Idle"}
          sub={payload ? `${payload.counts.hls_missing_slots} slots missing HLS` : ""}
          tone={payload?.hls.backfill_running ? "busy" : "idle"}
        />
        <SnapshotTile
          testid="kpi-chunk"
          label="Upload Session"
          value={payload ? formatBytes(payload.upload_limits.chunk_size_bytes) : "-"}
          sub={
            payload
              ? `${payload.upload_limits.stale_upload_session_seconds}s stale timeout`
              : ""
          }
        />
      </div>
    </section>
  );
}

function SnapshotTile({
  label,
  value,
  sub,
  tone = "muted",
  testid,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: Tone;
  testid: string;
}): ReactNode {
  return (
    <article
      data-testid={testid}
      data-tone={tone}
      className="rounded-panel border border-rule bg-surface px-3 py-2"
    >
      <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </p>
      <p className={`mt-1 truncate font-mono text-lg ${TONE_CLASSES[tone]}`}>{value}</p>
      {sub ? <p className="mt-0.5 truncate text-[0.65rem] text-ink-muted">{sub}</p> : null}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Background jobs — ported from the club-era /admin/performance strip,
// retargeted at the super-admin queue verbs.
// ---------------------------------------------------------------------------

function BackgroundJobsStrip({
  jobs,
  counts,
}: {
  jobs: AdminJob[];
  counts: Record<string, number>;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const visible = jobs.slice(0, 5);
  const failedCount = counts.failed ?? 0;
  const cleanup = useMutation({
    mutationFn: () => cleanupSuperAdminJobs({ status: "failed", limit: 500 }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: unscopedKey("super-jobs") }),
        queryClient.invalidateQueries({ queryKey: unscopedKey("super-diagnostics") }),
      ]);
      setCleanupConfirmOpen(false);
      toast.success("Failed jobs cleared", {
        description: `${result.deleted} failed job${result.deleted === 1 ? "" : "s"} removed from the queue log.`,
      });
    },
    onError: (err) =>
      toast.error("Couldn't clear failed jobs", {
        description: formatErr(err),
      }),
  });

  return (
    <section
      data-testid="super-ops-jobs"
      className="rounded-panel border border-rule bg-surface p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Background Jobs
          </h3>
          <p className="text-xs text-ink-muted">Queue health across every club.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <div className="flex flex-wrap gap-1 font-mono text-[0.6rem] uppercase tracking-wider">
            <span data-testid="super-ops-jobs-count-running" className="rounded-control border border-accent/40 px-2 py-1 text-accent">
              running {counts.running ?? 0}
            </span>
            <span data-testid="super-ops-jobs-count-pending" className="rounded-control border border-rule px-2 py-1 text-ink-muted">
              pending {counts.pending ?? 0}
            </span>
            <span data-testid="super-ops-jobs-count-failed" className="rounded-control border border-destructive/40 px-2 py-1 text-destructive">
              failed {failedCount}
            </span>
          </div>
          <AdminIconButton
            icon={Trash2}
            label={cleanup.isPending ? "Clearing failed jobs" : "Clear failed jobs"}
            data-testid="super-ops-jobs-clear-failed"
            onClick={() => setCleanupConfirmOpen(true)}
            disabled={failedCount === 0 || cleanup.isPending}
            tone="destructive"
          />
        </div>
      </div>
      {visible.length === 0 ? (
        <p className="mt-2 text-[0.65rem] font-mono text-ink-muted">No background jobs.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {visible.map((job) => (
            <li
              key={job.id}
              data-testid="super-ops-job-row"
              className="grid grid-cols-[auto_auto_1fr_auto] gap-2 rounded-inset border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
            >
              <span className="text-ink-muted">#{job.id}</span>
              <span className={job.status === "running" ? "text-accent" : "text-ink"}>{job.status}</span>
              <span className="truncate text-ink">{job.kind}</span>
              <span className="text-ink-muted">{job.team_id}</span>
            </li>
          ))}
        </ul>
      )}
      {cleanupConfirmOpen ? (
        <ConfirmDialog
          title="Clear failed jobs?"
          message="This removes up to 500 failed background-job rows from the operator queue log. It will not touch pending or running work."
          confirmLabel={cleanup.isPending ? "Clearing…" : "Clear failed jobs"}
          onConfirm={() => cleanup.mutate()}
          onClose={() => setCleanupConfirmOpen(false)}
          data-testid="super-ops-jobs-clear-confirm"
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Feeds — upload sessions, recent errors, recent activity (all cross-club).
// ---------------------------------------------------------------------------

function UploadSessionsCard({ payload }: { payload: AdminDiagnostics | undefined }) {
  const sessions = payload?.upload_sessions ?? [];
  return (
    <section
      data-testid="super-ops-uploads"
      className="rounded-panel border border-rule bg-surface p-3"
    >
      <SectionTitle
        title="Upload Sessions"
        detail="Chunked uploads currently moving (or recently finished) across the platform."
      />
      {sessions.length === 0 ? (
        <p className="mt-2 text-[0.65rem] font-mono text-ink-muted">No upload sessions.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {sessions.map((session) => (
            <li
              key={session.session_id}
              className="grid grid-cols-[auto_1fr_auto_auto] gap-2 rounded-inset border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
            >
              <span className={session.status === "active" ? "text-accent" : "text-ink-muted"}>
                {session.status}
              </span>
              <span className="truncate text-ink">
                {session.match_id} · {session.slot}
              </span>
              <span className="text-ink-muted">
                {typeof session.progress_pct === "number" ? `${session.progress_pct}%` : "—"}
              </span>
              <span className="text-ink-muted">
                {typeof session.idle_seconds === "number" ? `${Math.round(session.idle_seconds)}s idle` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentErrorsCard({ payload }: { payload: AdminDiagnostics | undefined }) {
  const errors = payload?.recent_errors ?? [];
  return (
    <section
      data-testid="super-ops-errors"
      className="rounded-panel border border-rule bg-surface p-3"
    >
      <SectionTitle title="Recent Errors" detail="Latest pipeline failures, newest first." />
      {errors.length === 0 ? (
        <p className="mt-2 text-[0.65rem] font-mono text-ink-muted">No recent errors.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {errors.map((error, index) => (
            <li
              key={`${error.match_id}-${error.slot}-${index}`}
              className="rounded-inset border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
            >
              <p className="text-destructive">{error.error_code}</p>
              <p className="truncate text-ink">{error.reason}</p>
              <p className="text-ink-muted">
                {error.match_id} · {error.slot}
                {error.timestamp ? ` · ${formatTime(error.timestamp)}` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentActivityCard({ payload }: { payload: AdminDiagnostics | undefined }) {
  const events = payload?.recent_activity ?? [];
  return (
    <section
      data-testid="super-ops-activity"
      className="rounded-panel border border-rule bg-surface p-3"
    >
      <SectionTitle title="Recent Activity" detail="Operator-visible platform events (72 h)." />
      {events.length === 0 ? (
        <p className="mt-2 text-[0.65rem] font-mono text-ink-muted">No recent activity.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {events.map((event, index) => (
            <li
              key={`${event.event_type}-${event.timestamp}-${index}`}
              className="grid grid-cols-[1fr_auto] gap-2 rounded-inset border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
            >
              <span className="min-w-0">
                <span className="text-ink">{event.message || event.event_type}</span>
                {event.actor ? <span className="text-ink-muted"> · {event.actor}</span> : null}
              </span>
              <span className="text-ink-muted">{formatTime(event.timestamp)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SectionTitle({ title, detail }: { title: string; detail: string }): ReactNode {
  return (
    <header>
      <h3 className="font-display text-sm uppercase tracking-wider text-ink">{title}</h3>
      <p className="text-xs text-ink-muted">{detail}</p>
    </header>
  );
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatErr(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Request failed";
}
